// Pure computation of which shop tabs should show a red "interest" dot.
// Consumed by both MobileTabBar and ScrollableNavbar. Two kinds:
//   'action' — a reward is claimable now; clears when the store shows it claimed
//   'new'    — unseen content hidden in a submenu; clears via localStorage seen-flag
// Kept free of Svelte / DOM / localStorage so it is unit-testable under node --test.
import { freeWebshopPacks } from './free-packs.js';

function adventInterest(s) {
    const e = s.adventCalendarEvent;
    if (Object.keys(e).length === 0 || !e.startAt) return null;
    const start = new Date(e.startAt);
    if (isNaN(start.getTime())) return null;
    // Which door is "today"? Mirrors AdventCalendar.svelte's dayDifference (UTC).
    const nowD = new Date(s.now);
    const startUTC = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate());
    const nowUTC = Date.UTC(nowD.getUTCFullYear(), nowD.getUTCMonth(), nowD.getUTCDate());
    const todayKey = `prize${Math.floor((nowUTC - startUTC) / 86_400_000) + 1}`;
    // Show the dot only if today's door exists and hasn't been claimed. The claimed
    // state comes from claimablePrizes (persisted) — NOT the session-only
    // currentAdventClaimed store, which is false on a fresh load even after a claim.
    // currentAdventClaimed still covers a just-claimed door before the next refetch.
    const todayExists = !!e.prizes?.[todayKey];
    const todayClaimed = s.currentAdventClaimed || !!e.claimablePrizes?.[todayKey];
    return (todayExists && !todayClaimed) ? { dot: true, kind: 'action' } : null;
}

function promosInterest(s) {
    const has = s.subscriptions.some(
        (sub) => freeWebshopPacks.includes(sub.bundleName) && !sub.collected,
    );
    return has ? { dot: true, kind: 'action' } : null;
}

// Owned (non-free-gift) subscription with a pending daily reward. `!collected`
// matches exactly when SubscriptionPack renders its "Collect" button.
function subCollectable(sub) {
    return !freeWebshopPacks.includes(sub.bundleName) && !sub.collected;
}
// Mirrors PackListing.subscriptionCondition: a "subscription" grants multiple
// items; a single-item (or item-less) entry is a "resource" subscription.
function isSubscriptionType(sub) {
    return sub.rewards?.items !== undefined && sub.rewards.items.length > 1;
}
function subscriptionsSubTab(s) {
    return s.subscriptions.some((sub) => subCollectable(sub) && isSubscriptionType(sub))
        ? { dot: true, kind: 'action' } : null;
}
function resourcesSubTab(s) {
    return s.subscriptions.some((sub) => subCollectable(sub) && !isSubscriptionType(sub))
        ? { dot: true, kind: 'action' } : null;
}

function growthCapitalSubTab(s) {
    const levels = s.growthFundConfig?.levels ? Object.keys(s.growthFundConfig.levels) : [];
    const claimable = s.playerGrowthFund && levels.some((l) => {
        const level = parseInt(l);
        return s.castleLevel >= level && !s.claimedGrowthFundPrizes.includes(level);
    });
    return claimable ? { dot: true, kind: 'action' } : null;
}
function partisanProgressSubTab(s) {
    const levels = s.monsterGrowthFundConfig?.levels ? Object.keys(s.monsterGrowthFundConfig.levels) : [];
    const claimable = s.playerMonsterGrowthFund && levels.some((l) => {
        const level = parseInt(l);
        return s.defeatedMonsterLevels.includes(level) && !s.claimedMonsterGrowthFundPrizes.includes(level);
    });
    return claimable ? { dot: true, kind: 'action' } : null;
}

function goldRushInterest(s) {
    const e = s.rechargeRewardsEvent;
    if (Object.keys(e).length === 0 || !e.prizes) return null;
    const has = Object.keys(e.prizes).some((key) => {
        const id = parseInt(key.replace('prize', ''));
        const claimable = e.claimablePrizes?.[key];
        return claimable && claimable.claimed === 0 && id < e.currentPrizeId;
    });
    return has ? { dot: true, kind: 'action' } : null;
}

// Shared battle-pass "has a claimable reward" test — mirrors BattlePassHeader's
// `hasAnyClaimable`, i.e. exactly what enables the "Claim All" button. Only an
// already-earned, unclaimed PRIZE counts. The daily bonus is deliberately
// EXCLUDED: it's a separate recurring button that's available almost every day,
// so including it kept the tab dot lit permanently ("never goes away") even when
// there was nothing on the track left to claim.
function battlePassClaimable(e, now) {
    const claimWindowOver = e.claimBy ? new Date(e.claimBy).getTime() <= now : false;
    if (claimWindowOver) return false;
    return Object.entries(e.prizes || {}).some(([key, prize]) => {
        if (e.points < prize.pointsNeeded) return false;
        const c = e.claimablePrizes?.[key];
        return c?.claimed === 0 || (e.hasEventPass && c?.seasonPassClaimed === 0);
    });
}

function seasonPassInterest(s) {
    const e = s.battlePassEvent;
    if (Object.keys(e).length === 0) return null;
    // Tab only shows while the claim window is open (mirrors deriveShopTabs).
    if (!(e.claimBy && new Date(e.claimBy).getTime() > s.now)) return null;
    return battlePassClaimable(e, s.now) ? { dot: true, kind: 'action' } : null;
}

// 'new' sub-tabs hidden in the Events submenu. The Events "Battle Pass" and
// "Special Events" sub-tabs are purchasable pack lists (not the claimable
// battlePassEvent), so they carry no dot — the Season Pass tab handles claims.
function cardCollectionSubTab(s) {
    const unseen = s.activeStorePacks.some((p) => p.storeSection === 'cardCollection')
        && s.seen.cardCollectionSeen !== 'true';
    return unseen ? { dot: true, kind: 'new' } : null;
}
function dividedNationSubTab(s) {
    const unseen = s.activeStorePacks.some((p) => p.storeSection === 'pvpArena')
        && s.seen.dividedNationSeen !== 'true';
    return unseen ? { dot: true, kind: 'new' } : null;
}
function singleJackFeverSubTab(s) {
    const unseen = s.activeStorePacks.some((p) => p.storeSection === 'singleJackFever')
        && s.seen.singleJackFeverSeen !== 'true';
    return unseen ? { dot: true, kind: 'new' } : null;
}
// Chain Deals is gated on the event, not on packs (its packs are hidden).
function chainDealsSubTab(s) {
    const unseen = !!s.chainPromoEvent?.id && s.seen.chainDealsSeen !== 'true';
    return unseen ? { dot: true, kind: 'new' } : null;
}

export function deriveTabInterest(state) {
    const s = {
        activeStorePacks: [],
        subscriptions: [],
        adventCalendarEvent: {},
        currentAdventClaimed: false,
        rechargeRewardsEvent: {},
        battlePassEvent: {},
        chainPromoEvent: {},
        growthFundConfig: null,
        claimedGrowthFundPrizes: [],
        playerGrowthFund: false,
        castleLevel: 0,
        monsterGrowthFundConfig: null,
        claimedMonsterGrowthFundPrizes: [],
        playerMonsterGrowthFund: false,
        defeatedMonsterLevels: [],
        seen: {},
        now: 0,
        ...state,
    };

    const result = {};
    const set = (key, interest) => { if (interest) result[key] = interest; };

    set('adventCalendar', adventInterest(s));
    set('promos', promosInterest(s));

    // Gold & Resources — per sub-tab; the parent tab aggregates them.
    const subs = subscriptionsSubTab(s);
    const resources = resourcesSubTab(s);
    set('subscriptions', subs);
    set('resources', resources);
    set('goldAndResources', subs || resources);

    // Advancement Rewards — per sub-tab; parent aggregates. (Resource Trove is
    // purchase-only, no claim, so it never carries a dot.)
    const growth = growthCapitalSubTab(s);
    const partisan = partisanProgressSubTab(s);
    set('growthCapital', growth);
    set('partisanProgress', partisan);
    set('advancementRewards', growth || partisan);

    set('goldRush', goldRushInterest(s));
    set('webBattlePass', seasonPassInterest(s));

    // Events — per sub-tab; parent aggregates the unseen 'new' sub-tabs.
    const card = cardCollectionSubTab(s);
    const divided = dividedNationSubTab(s);
    const chain = chainDealsSubTab(s);
    const singleJack = singleJackFeverSubTab(s);
    set('cardCollection', card);
    set('dividedNation', divided);
    set('chainDeals', chain);
    set('singleJackFever', singleJack);
    set('specialEvents', card || divided || chain || singleJack);

    return result;
}
